import { TextArea } from '@storeblocks/input';
import Select, { OptionProps } from '@storeblocks/select';
import { useFormikContext } from 'formik';
import { type ChangeEvent, type FC } from 'react';

import { FormValidationSummary } from '@/components/Form/FormValidationSummary';
import { formHasErrors } from '@/components/Form/utils/formHasErrors';
import { Column, Row } from '@/components/Grid';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { nameof } from '@/util/nameof';

import { useDiscretionaryPortfolios } from '../../hooks/useDiscretionaryPortfolios';
import { WithdrawalOrderFormValues } from '../../types/WithdrawalOrderFormValues';
import { NavigationButtons } from '../NavigationButtons';
import { SignatoryOptions } from '../SignatoryOptions';
import { SinglePortfolioBankAccount } from '../SinglePortfolioBankAccount';
import { ExpectedTransferDateSelector } from './ExpectedTransferDateSelector';
import { WithdrawalAmountInput } from './WithdrawalAmountInput';
import { WithdrawalExternalBankAccountSelect } from './WithdrawalExternalBankAccountSelect';
import { WithdrawalPortfolioBankAccountSelect } from './WithdrawalPortfolioBankAccountSelect';

export const WithdrawalOrderFormContent: FC = () => {
  const texts = useTexts();

  const { values, touched, errors, getFieldProps, setFieldValue, submitCount } =
    useFormikContext<WithdrawalOrderFormValues>();

  const portfolios = useDiscretionaryPortfolios();

  const options = portfolios.map<OptionProps>((portfolio) => ({
    value: portfolio.id,
    label: `${portfolio.id} - ${portfolio.name}`,
  }));

  const [selectedPortfolio] = portfolios.filter(
    (portfolio) => portfolio.id === values.portfolioId,
  );

  const handlePortfolioChanged = async (
    event: ChangeEvent<HTMLSelectElement>,
  ): Promise<void> => {
    const { onChange } = getFieldProps(
      nameof<WithdrawalOrderFormValues>('portfolioId'),
    );
    onChange(event);

    const portfolioId = event.target.value;
    const portfolioBankAccounts = portfolios.filter(
      (portfolio) => portfolio.id === portfolioId,
    )[0].portfolioBankAccounts;

    if (portfolioBankAccounts.length === 1) {
      await setFieldValue(
        nameof<WithdrawalOrderFormValues>('fromPortfolioBankAccount'),
        portfolioBankAccounts[0].accountNumber,
      );

      await setFieldValue(
        nameof<WithdrawalOrderFormValues>('currency'),
        portfolioBankAccounts[0].currency,
      );
    }
  };

  return (
    <WithGap gap="64">
      <WithGap gap="32">
        {portfolios.length > 1 && (
          <Row>
            <Column $span={6}>
              <Select
                {...getFieldProps(
                  nameof<WithdrawalOrderFormValues>('portfolioId'),
                )}
                id="portfolio-selector"
                label={texts.orders.form.portfolio.title}
                hint={texts.orders.form.portfolio.message}
                placeholder={texts.orders.form.portfolio.placeholder}
                alertMessageType={
                  touched.portfolioId && errors.portfolioId
                    ? 'error'
                    : undefined
                }
                alertMessage={errors.portfolioId}
                options={options}
                fluid
                onChange={handlePortfolioChanged}
              />
            </Column>
          </Row>
        )}

        {values.portfolioId && (
          <Row>
            <Column $span={6}>
              {selectedPortfolio.portfolioBankAccounts.length > 1 ? (
                <WithdrawalPortfolioBankAccountSelect
                  portfolioId={selectedPortfolio.id}
                />
              ) : (
                <SinglePortfolioBankAccount
                  portfolioBankAccountNumber={
                    selectedPortfolio.portfolioBankAccounts[0].accountNumber
                  }
                />
              )}
            </Column>
          </Row>
        )}
      </WithGap>

      <Row>
        <Column $span={6}>
          <WithdrawalExternalBankAccountSelect />
        </Column>
      </Row>

      <WithGap gap="32">
        <Row>
          <Column $span={6}>
            <WithdrawalAmountInput />
          </Column>
        </Row>

        <Row>
          <Column $span={6}>
            <ExpectedTransferDateSelector />
          </Column>
        </Row>

        <Row>
          <Column $span={6}>
            <TextArea
              {...getFieldProps(
                nameof<WithdrawalOrderFormValues>('messageToSam'),
              )}
              id="message-to-sam"
              label={texts.orders.form.messageToSam.title}
              hint={{
                text: texts.orders.form.messageToSam.description.withdrawal,
              }}
              alert={{
                messageType:
                  touched.messageToSam && errors.messageToSam
                    ? 'error'
                    : undefined,
                message: errors.messageToSam,
              }}
              fluid
            />
          </Column>
        </Row>

        <SignatoryOptions />

        {submitCount > 0 && formHasErrors(errors) && (
          <FormValidationSummary
            title={texts.orders.form.errors.validationSummary.title}
            errors={errors}
          />
        )}

        {values.portfolioId && <NavigationButtons />}
      </WithGap>
    </WithGap>
  );
};
